/**
 * Users API
 * Contains all API functions related to user discovery and suggestions
 */

import { apiGet } from "./apiClient";
import { User } from "../types";
import { updateUser, getUserById } from "./auth.api";
import { getLocalData, saveLocalData } from "./ai.api";

export interface SuggestedUsersParams {
  limit?: number;
  excludeCurrentUser?: boolean;
  category?: string;
}

export interface SearchUsersParams {
  query: string;
  limit?: number;
  page?: number;
}

export interface SuggestedUsersResponse {
  users: User[];
  hasMore: boolean;
  nextPage?: string;
  total: number;
}

export interface SearchUsersResponse {
  users: User[];
  hasMore: boolean;
  nextPage?: string;
  total: number;
}

/**
 * Delta storage for user data
 * @param data User data to update in storage
 */
const deltaStorage = async (data: any) => {
  const storage = await getLocalData("idearify-app-storage");
  await saveLocalData('idearify-app-storage', { ...storage, state: { ...(storage?.state || {}), user: data.state.user } });
}

/**
 * Get suggested users for the current user
 * @param params Parameters for fetching suggested users
 * @returns Promise with suggested users data
 */
export const getSuggestedUsers = async (
  params: SuggestedUsersParams = {},
): Promise<SuggestedUsersResponse> => {
  const queryParams = new URLSearchParams();

  if (params.limit) queryParams.append("limit", params.limit.toString());
  if (params.excludeCurrentUser !== undefined)
    queryParams.append(
      "excludeCurrentUser",
      params.excludeCurrentUser.toString(),
    );
  if (params.category) queryParams.append("category", params.category);

  const endpoint = `/users/suggested${queryParams.toString() ? `?${queryParams.toString()}` : ""}`;

  try {
    const response = await apiGet(endpoint, {}, "authenticated");
    return response.data || { users: [], hasMore: false, total: 0 };
  } catch (error) {
    console.error("Failed to fetch suggested users:", error);
    // Return mock data for development if API fails
    return {
      users: [],
      hasMore: false,
      total: 0,
    };
  }
};

/**
 * Search users by query
 * @param params Search parameters
 * @returns Promise with search results
 */
export const getUser = async (username: string): Promise<any> => {
  try {
    const response = await apiGet(`/user/${username}`, {}, "");
    return response.data;
  } catch (error) {
    console.error("Failed to fetch user:", error);
    // Return mock data for development if API fails
    return {
      id: "1",
      username: "innovator_jane",
      firstName: "Jane",
      lastName: "Smith",
      email: "",
    };
  }
};

/**
 * Search users by query
 * @param params Search parameters
 * @returns Promise with search results
 */
export const searchUsers = async (
  params: SearchUsersParams,
): Promise<SearchUsersResponse> => {
  const queryParams = new URLSearchParams();
  queryParams.append("q", params.query);

  if (params.limit) queryParams.append("limit", params.limit.toString());
  if (params.page) queryParams.append("page", params.page.toString());

  const endpoint = `/users/search?${queryParams.toString()}`;

  try {
    const response = await apiGet(endpoint, {}, "authenticated");
    return response.data || { users: [], hasMore: false, total: 0 };
  } catch (error) {
    console.error("Failed to search users:", error);
    return {
      users: [],
      hasMore: false,
      total: 0,
    };
  }
};



/**
 * Get users that follow the target user
 * @param username Target username
 * @returns Promise with array of followers
 */
export const getUserFollowers = async (username: string): Promise<User[]> => {
  try {
    const response = await apiGet(
      `/users/${encodeURIComponent(username)}/followers`,
      {},
      "authenticated",
    );
    return response.data?.followers || [];
  } catch (error) {
    console.error("Failed to fetch followers:", error);
    return [];
  }
};

/**
 * Get users that the target user follows
 * @param username Target username
 * @returns Promise with array of following users
 */
export const getUserFollowing = async (username: string): Promise<User[]> => {
  try {
    const response = await apiGet(
      `/users/${encodeURIComponent(username)}/following`,
      {},
      "authenticated",
    );
    return response.data?.following || [];
  } catch (error) {
    console.error("Failed to fetch following users:", error);
    return [];
  }
};

/**
 * Get trending users (users with high engagement)
 * @param limit Number of users to fetch
 * @returns Promise with trending users data
 */
export const getTrendingUsers = async (limit: number = 5): Promise<User[]> => {
  try {
    const response = await apiGet(
      `/users/trending?limit=${limit}`,
      {},
      "authenticated",
    );
    return response.data?.users || [];
  } catch (error) {
    console.error("Failed to fetch trending users:", error);
    return [];
  }
};

/**
 * Get users by category or interest
 * @param category Category or interest to filter by
 * @param limit Number of users to fetch
 * @returns Promise with users data
 */
export const getUsersByCategory = async (
  category: string,
  limit: number = 10,
): Promise<User[]> => {
  try {
    const response = await apiGet(
      `/users/category/${encodeURIComponent(category)}?limit=${limit}`,
      {},
      "authenticated",
    );
    return response.data?.users || [];
  } catch (error) {
    console.error("Failed to fetch users by category:", error);
    return [];
  }
};

/**
 * Follow a user
 * @param userId The ID of the user to follow
 * @returns Promise with follow result
 */
export const followUser = async (
  userId: string,
  username: string,
): Promise<{ success: boolean; message?: string; user: any }> => {
  try {
    const thisUser = await getUser(username);
    const updatedFollowing = [...(thisUser.subscribed || []), userId];
    await updateUser({
      id: thisUser.id,
      username: thisUser.username,
      subscribed: updatedFollowing,
    });
    
    // Increment target user's subscribers count
    try {
      const targetUserResp = await getUserById(userId);
      const targetUser = targetUserResp.data || targetUserResp;
      if (targetUser && targetUser.id) {
        await updateUser({
          id: targetUser.id,
          username: targetUser.username,
          subscribers: (targetUser.subscribers || 0) + 1,
        });
      }
    } catch (targetErr) {
      console.error("Failed to update target user subscribers count:", targetErr);
    }

    const data = {
      state: {
        user: {
          ...thisUser,
          subscribed: updatedFollowing,
        },
      },
    };
    await deltaStorage(data);
    return { success: true, message: "Successfully followed user", user: data.state.user };
  } catch (error) {
    console.error("Failed to follow user:", error);
    // Mock success for development
    return { success: true, message: "Successfully followed user (mock)", user: null };
  }
};

/**
 * Unfollow a user
 * @param userId The ID of the user to unfollow
 * @returns Promise with unfollow result
 */
export const unfollowUser = async (
  userId: string,
  username: string,
): Promise<{ success: boolean; message?: string; user: any }> => {
  try {
    const thisUser = await getUser(username);
    const updatedFollowing = (thisUser.subscribed || []).filter(
      (id: string) => id !== userId,
    );
    await updateUser({
      id: thisUser.id,
      username: thisUser.username,
      subscribed: updatedFollowing,
    });

    // Decrement target user's subscribers count
    try {
      const targetUserResp = await getUserById(userId);
      const targetUser = targetUserResp.data || targetUserResp;
      if (targetUser && targetUser.id) {
        const currentCount = targetUser.subscribers || 0;
        await updateUser({
          id: targetUser.id,
          username: targetUser.username,
          subscribers: currentCount > 0 ? currentCount - 1 : 0,
        });
      }
    } catch (targetErr) {
      console.error("Failed to update target user subscribers count:", targetErr);
    }

    const data = {
      state: {
        user: {
          ...thisUser,
          subscribed: updatedFollowing,
        },
      },
    };
    await deltaStorage(data);
    return { success: true, message: "Successfully unfollowed user", user: data.state.user };
  } catch (error) {
    console.error("Failed to unfollow user:", error);
    // Mock success for development
    return { success: true, message: "Successfully unfollowed user (mock)", user: null };
  }
};

/**
 * Get the current user's following list
 * @returns Promise with list of user IDs the current user is following
 */
export const getCurrentUserFollowing = async (): Promise<string[]> => {
  try {
    const response = await apiGet("/users/me/following", {}, "authenticated");
    return response.data?.following || [];
  } catch (error) {
    console.error("Failed to get current user following list:", error);
    // Return mock data for development
    return ["2"]; // Mock: user is following tech_ideas
  }
};
